import React, { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Star } from 'lucide-react';
import { useAuth } from '@/features/auth/hooks';
import { useService } from '@/features/services/hooks/useServices';
import { LoadingSpinner } from '@/components/common/LoadingSpinner/LoadingSpinner';
import type { ServiceDetailResponse } from '@/types/services';
import { useCreateSubscriptionMutation, useCancelSubscriptionMutation, useGetServiceReviewsQuery } from '@/services/api/services/servicesApi';
import { formatCurrencyInCents } from '@/utils/helpers';
import { ShareExperienceSection } from '@/features/services/components/ShareExperienceSection';
import { WriteReviewModal } from '@/features/services/components/WriteReviewModal';
import { useToast } from '@/components/ui/toast-provider';

/** Service icon with fallback to letter avatar */
const ServiceIcon: React.FC<{ name: string; iconUrl?: string }> = ({ name, iconUrl }) => {
  const [error, setError] = useState(false);
  if (iconUrl && !error) {
    return (
      <img
        src={iconUrl}
        alt={`${name} icon`}
        className="w-20 h-20 rounded-xl object-contain"
        onError={() => setError(true)}
      />
    );
  }
  return (
    <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-blue-900 to-blue-500 flex items-center justify-center text-white text-3xl font-bold">
      {name.charAt(0)}
    </div>
  );
};

/**
 * ServiceDetails displays the details of a selected service, following the Figma design.
 */



const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'pricing', label: 'Pricing' },
  { key: 'reviews', label: 'Rating & Reviews' },
];

const SORT_OPTIONS = [
  { value: 'recent', label: 'Most Recent' },
  { value: 'rating_high', label: 'Highest Rated' },
  { value: 'rating_low', label: 'Lowest Rated' },
];

/**
 * Pricing plan interface for the component
 */
interface ComponentPricingPlan {
  id: string;
  name: string;
  price: string;
  unit: string;
  description: string;
  features: string[];
  cta: string;
  highlight: boolean;
}

/**
 * Get user initials from name
 */
const getUserInitials = (name: string): string => {
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
  }
  return name.charAt(0).toUpperCase() + name.charAt(1).toLowerCase();
};

/**
 * Transform ServiceDetailResponse data to component format
 */
const transformServiceData = (service: ServiceDetailResponse) => {
  // Extract features from tools_metadata if available
  const getFeatures = (key_features: string): string[] => {
    if (key_features) {
      return key_features.split('\n');
    }
    return [];
  };

  // Extract use cases from tools_metadata if available
  const getUseCases = (use_cases: string): string[] => {
    if (use_cases) {
      return use_cases.split('\n');
    }
    return [];
  };

  // Transform pricing plans - for now return empty array since API spec doesn't include pricing
  const pricingPlans: ComponentPricingPlan[] = [];

  // Get developer name - prefer company_name, fallback to email
  const getDeveloperName = (): string => {
    if (service.developer.company_name) {
      return service.developer.company_name;
    }
    // Extract name from email
    const emailPart = service.developer.email.split('@')[0];
    return emailPart.charAt(0).toUpperCase() + emailPart.slice(1).replace(/[._]/g, ' ');
  };

  // Get category name
  const getCategoryName = (): string => {
    if (service.category_details?.name) {
      return service.category_details.name;
    }
    return service.category || 'Uncategorized';
  };

  return {
    id: service.id,
    name: service.name,
    icon_url: service.icon_url,
    provider: getDeveloperName(),
    category: getCategoryName(),
    description: service.description,
    overview: service.description, // Use description as overview since API doesn't have separate overview
    rating: service.rating_summary.average_rating,
    ratingCount: service.rating_summary.total_reviews,
    features: getFeatures(service.key_features),
    use_cases: getUseCases(service.use_cases),
    pricing: pricingPlans,
    plans: service.plans,
    user_context: service.user_context,
    subscribed_plans: service.subscribed_plans,
    user_plan_contexts: (service as unknown as { user_plan_contexts?: unknown }).user_plan_contexts,
    // Rating summary (no longer includes reviews list)
    ratingData: {
      average: service.rating_summary.average_rating,
      total: service.rating_summary.total_reviews,
      breakdown: [
        { stars: 5, percent: Math.round((service.rating_summary.distribution['5'] / service.rating_summary.total_reviews) * 100) },
        { stars: 4, percent: Math.round((service.rating_summary.distribution['4'] / service.rating_summary.total_reviews) * 100) },
        { stars: 3, percent: Math.round((service.rating_summary.distribution['3'] / service.rating_summary.total_reviews) * 100) },
        { stars: 2, percent: Math.round((service.rating_summary.distribution['2'] / service.rating_summary.total_reviews) * 100) },
        { stars: 1, percent: Math.round((service.rating_summary.distribution['1'] / service.rating_summary.total_reviews) * 100) },
      ],
    },
  };
};

/**
 * Type guard to check if plan_details has interval property
 */
function hasInterval(details: object): details is { interval: string } {
  return typeof (details as { interval?: unknown }).interval === 'string';
}

const ServiceDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  const { showToast } = useToast();

  // Fetch service data from API using the useService hook
  const {
    service: serviceData,
    isLoading,
    error,
    refetch,
  } = useService(id!);

  const getTabFromQuery = React.useCallback(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab === 'overview' || tab === 'pricing' || tab === 'reviews') {
      return tab;
    }
    return 'overview';
  }, [location.search]);

  const [activeTab, setActiveTab] = useState<'overview' | 'pricing' | 'reviews'>(getTabFromQuery);

  // Review filter/sort/pagination state
  const [selectedStar, setSelectedStar] = useState<number | null>(null);
  const [sortOption, setSortOption] = useState<string>('recent');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const REVIEWS_PER_PAGE = 4;

  // Review modal state
  const [isReviewModalOpen, setIsReviewModalOpen] = useState<boolean>(false);

  const handleOpenReviewModal = () => {
    setIsReviewModalOpen(true);
  };

  const handleCloseReviewModal = () => {
    setIsReviewModalOpen(false);
  };

  const handleReviewSuccess = () => {
    // Refetch reviews data to get updated reviews
    refetchReviews();
    // Also refetch service data to update overall rating
    refetch();
  };

  React.useEffect(() => {
    setActiveTab(getTabFromQuery());
  }, [getTabFromQuery]);

  // Subscription mutation hook (must be at top level)
  const [createSubscription, { isLoading: isSubscribing, isSuccess: isSubscribedSuccess, reset }] = useCreateSubscriptionMutation();

  // Track which plan is being subscribed to and which plan is subscribed
  const [subscribingPlanId, setSubscribingPlanId] = useState<string | null>(null);
  // Track which plan is being unsubscribed
  const [unsubscribingPlanId, setUnsubscribingPlanId] = useState<string | null>(null);

  // Reset subscribingPlanId and set subscribedPlanId on success
  React.useEffect(() => {
    if (isSubscribedSuccess && subscribingPlanId) {
      setSubscribingPlanId(null);
      // Optionally, reset mutation state for next use
      reset();
    }
  }, [isSubscribedSuccess, subscribingPlanId, reset]);

  const [cancelSubscription, { isLoading: isUnsubscribing }] = useCancelSubscriptionMutation();

  // Fetch reviews separately with filtering and sorting
  const {
    data: reviewsResponse,
    isLoading: reviewsLoading,
    error: reviewsError,
    refetch: refetchReviews,
  } = useGetServiceReviewsQuery({
    serviceId: id!,
    page: currentPage,
    limit: REVIEWS_PER_PAGE,
    rating_filter: selectedStar || undefined,
    sort: sortOption as 'recent' | 'rating_high' | 'rating_low',
  });

  // Handle loading state
  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto py-4 px-4 md:px-0">
        <LoadingSpinner size="lg" text="Loading service details..." />
      </div>
    );
  }

  // Handle error state
  if (error || !serviceData) {
    return (
      <div className="max-w-5xl mx-auto py-4 px-4 md:px-0">
        <div className="text-center py-8">
          <p className="text-red-600 mb-4">
            {error ? 'Failed to load service details' : 'Service not found'}
          </p>
          <div className="flex gap-4 justify-center">
            <Button onClick={() => refetch()} variant="outline">
              Try Again
            </Button>
            <Button onClick={() => navigate('/')}>
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Handle missing service ID
  if (!id) {
    return (
      <div className="max-w-5xl mx-auto py-4 px-4 md:px-0">
        <div className="text-center py-8">
          <p className="text-red-600 mb-4">Invalid service ID</p>
          <Button onClick={() => navigate('/')}>
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  // Transform API data to component format
  const service = transformServiceData(serviceData);

  // Get rating summary from service data
  const ratingData = service.ratingData;

  // Transform reviews from API response
  const transformedReviews = reviewsResponse?.data.map(review => ({
    id: parseInt(review.id),
    user: {
      initials: getUserInitials(review.user.name),
      name: review.user.name
    },
    rating: review.rating,
    title: review.title || 'Review',
    date: new Date(review.created_at).toLocaleDateString(),
    verified: review.is_verified,
    text: review.comment || 'No comment provided',
  })) || [];

  // Get pagination info from API response
  const totalPages = reviewsResponse?.pagination.total_pages || 1;

  const handleTryNow = () => {
    if (!auth.isAuthenticated) {
      navigate('/login', { replace: true, state: { from: location } });
      return;
    }
    setActiveTab('pricing');
  };

  return (
    <div className="max-w-5xl mx-auto py-4 px-4 md:px-0">
      {/* Breadcrumb removed, handled by Layout */}

      {/* Header */}
      <div className="relative mb-8">
        {/* Try Now button top right - Only show if user is not subscribed */}
        {!service.user_context?.is_subscribed && (
          <div className="absolute right-0 top-0">
            <Button size="lg" className="px-8 py-2 rounded-lg text-base font-semibold bg-blue-600 hover:bg-blue-700" onClick={handleTryNow}>Try Now</Button>
          </div>
        )}
        {/* Main row: avatar, name, provider, rating */}
        <div className="flex items-center gap-6">
          <ServiceIcon name={service.name} iconUrl={service.icon_url} />
          <div className="flex flex-col justify-center flex-1">
            <div className="flex items-center gap-4">
              <h1 className="text-4xl font-extrabold text-gray-900 leading-tight">{service.name}</h1>
            </div>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-gray-500 text-base">by</span>
              <span className="text-blue-600 text-base font-semibold cursor-pointer hover:underline">{service.provider}</span>
              <span className="flex items-center gap-1 ml-2 text-base font-medium">
                <Star className="w-5 h-5 fill-yellow-400 text-yellow-400 mr-1" />
                <span className="text-gray-900 font-semibold">{service.rating.toFixed(1)}</span>
              </span>
            </div>
          </div>
        </div>
        {/* Category pill in its own row */}
        <div className="mt-6">
          <span className="inline-block bg-gray-100 text-gray-700 text-base font-medium rounded-full px-5 py-2">{service.category}</span>
        </div>
        {/* Description */}
        <div className="mt-6 text-gray-600 text-lg leading-relaxed max-w-3xl">
          {service.description}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-8">
        <nav className="flex gap-8 -mb-px">
          {TABS.map(tab => (
            <button
              key={tab.key}
              className={`py-2 px-1 border-b-2 font-semibold transition-colors ${
                activeTab === tab.key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-blue-600'
              }`}
              onClick={() => {
                navigate(`?tab=${tab.key}`, { replace: true });
              }}
              type="button"
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[600px]">
        {activeTab === 'overview' && (
          <div className="w-full">
            <Card className="mb-6">
              <CardContent className="py-8">
                <h2 className="text-2xl font-bold mb-4 text-gray-900">Service Overview</h2>
                <p className="text-gray-700 mb-6 text-base leading-relaxed">{service.overview}</p>
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3 text-gray-900">Key Features</h3>
                  <ul className="list-disc pl-6 space-y-2 text-gray-800 text-base">
                    {service.features.map((feature, index) => (
                      <li key={`feature-${index}-${feature}`}>{feature}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-3 text-gray-900">Use Cases</h3>
                  <ul className="list-disc pl-6 space-y-3 text-gray-700 text-lg">
                    {service.use_cases.map((use_case) => {
                      const [category, ...descParts] = use_case.split(":");
                      const description = descParts.join(":").trim();
                      return (
                        <li key={use_case}>
                          <span className="font-bold">{category}{description ? ":" : ""}</span>
                          {description && <span> {description}</span>}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'pricing' && (
          <>
            {service.plans.length > 0 ? (
              <>
                <div className="text-left text-gray-500 text-base mb-8">
                  Choose the plan that best fits your usage needs. All plans include core functionality with different API rate limits and features.
                </div>
                <div className="grid md:grid-cols-3 gap-8">
                  {service.plans.map((plan) => {
                    // Find the plan context for this plan

                    const subscribedPlan = (service.subscribed_plans || []).find(p => p.plan_id === plan.id && p.status === 'active');
                    const isThisPlanSubscribed = !!subscribedPlan;
                    const isThisPlanSubscribing = subscribingPlanId === plan.id && isSubscribing;
                    const isThisPlanUnsubscribing = unsubscribingPlanId === plan.id && isUnsubscribing;
                    return (
                      <Card
                        key={plan.id}
                        className={`mb-6 flex flex-col h-full ${plan.id ? 'border-2 border-blue-600 shadow-lg' : ''}`}
                      >
                        <CardContent className="py-8 flex flex-col h-full">
                          <h3 className="text-xl font-bold mb-2 text-gray-900 text-center">{plan.name}</h3>
                          <div className="flex items-end justify-center mb-2">
                            <span className="text-4xl font-extrabold text-blue-600">{formatCurrencyInCents(plan.plan_details.amount_cents, plan.plan_details.currency)}</span>
                            <span className="ml-1 text-base text-gray-400 font-medium">/{hasInterval(plan.plan_details) ? plan.plan_details.interval : plan.plan_details.billingPeriod}</span>
                          </div>
                          <p className="text-gray-700 text-center mb-4">{plan.description}</p>
                          <ul className="list-disc pl-5 space-y-1 text-gray-700 mb-6">
                            {/* {plan.features.map((feature, index) => (
                              <li key={`plan-${plan.id}-feature-${index}-${feature}`}>{feature}</li>
                            ))} */}
                          </ul>
                          {plan.id ? (
                            isThisPlanSubscribed ? (
                              <Button
                                className="mt-auto w-full text-gray-900 bg-white border border-gray-300 focus:outline-none hover:bg-gray-100 focus:ring-4 focus:ring-gray-100 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2 dark:bg-gray-800 dark:text-white dark:border-gray-600 dark:hover:bg-gray-700 dark:hover:border-gray-600 dark:focus:ring-gray-700"
                                size="lg"
                                onClick={async () => {

                                  // Show confirmation dialog
                                  const isConfirmed = window.confirm('Are you sure you want to cancel this subscription?');
                                  if (!isConfirmed) {
                                    return;
                                  }

                                  setUnsubscribingPlanId(plan.id);
                                  try {
                                    await cancelSubscription(subscribedPlan.id).unwrap();

                                    // Show success toast
                                    showToast({
                                      type: 'success',
                                      title: 'Successfully unsubscribed!',
                                      message: `You have been unsubscribed from the ${plan.plan_name} plan.`,
                                    });
                                  } catch {
                                    // Show error toast
                                    showToast({
                                      type: 'error',
                                      title: 'Unsubscription failed',
                                      message: 'Failed to unsubscribe from the plan. Please try again.',
                                    });
                                  } finally {
                                    setUnsubscribingPlanId(null);
                                  }
                                }}
                                disabled={isThisPlanUnsubscribing}
                              >
                                {isThisPlanUnsubscribing ? 'Unsubscribing...' : 'Unsubscribe'}
                              </Button>
                            ) : (
                              <Button
                                className="mt-auto w-full bg-blue-600 text-white hover:bg-blue-700"
                                size="lg"
                                onClick={async () => {
                                  setSubscribingPlanId(plan.id);
                                  try {
                                    await createSubscription({
                                      plan_id: plan.id,
                                      metadata: { source: 'marketplace_ui', user_agent: navigator.userAgent },
                                    }).unwrap();

                                    // Show success toast
                                    showToast({
                                      type: 'success',
                                      title: 'Successfully subscribed!',
                                      message: `You are now subscribed to the ${plan.name} plan.`,
                                    });
                                  } catch {
                                    setSubscribingPlanId(null);

                                    // Show error toast
                                    showToast({
                                      type: 'error',
                                      title: 'Subscription failed',
                                      message: 'Failed to subscribe to the plan. Please try again.',
                                    });
                                  }
                                }}
                                disabled={isSubscribing && subscribingPlanId === plan.id}
                              >
                                {isThisPlanSubscribing ? 'Subscribing...' : 'Subscribe'}
                              </Button>
                            )
                          ) : (
                            <button
                              className="mt-auto w-full rounded-lg bg-blue-50 text-blue-600 font-semibold py-3 text-base transition hover:bg-blue-100"
                              type="button"
                            >
                              {plan.name}
                            </button>
                          )}

                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-500 text-lg mb-4">No pricing plans available</div>
                <p className="text-gray-400 text-base">
                  Pricing information for this service is not yet configured. Please contact the service provider for more details.
                </p>
              </div>
            )}
          </>
        )}

        {activeTab === 'reviews' && (
          <div className="w-full">
            <Card className="mb-6 max-w-full">
              <CardContent className="py-8 px-2 sm:px-4 md:px-8 max-w-full">
                <div className="bg-gray-50 rounded-2xl p-4 sm:p-6 md:p-10 mb-10 min-w-0">
                  {/* First row: summary + breakdown */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:gap-8 min-w-0">
                    {/* Left: Average, stars, review count */}
                    <div className="flex flex-col items-center justify-center text-center w-full sm:w-64 mb-8 sm:mb-0 min-w-0">
                      <span className="text-6xl font-extrabold text-blue-600 mb-2">{ratingData.average.toFixed(1)}</span>
                      <div className="flex gap-1 mb-2">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
                        ))}
                      </div>
                      <div className="text-gray-500 text-base font-medium">Based on {ratingData.total.toLocaleString()} reviews</div>
                    </div>
                    {/* Right: Breakdown */}
                    <div className="flex-1 min-w-0 w-full">
                      {ratingData.breakdown.map((row: { stars: number; percent: number }) => (
                        <div key={row.stars} className="flex items-center gap-2 mb-3 min-w-0">
                          <span className="text-sm text-gray-700 font-medium text-left min-w-[56px]">{row.stars} star{row.stars > 1 ? 's' : ''}</span>
                          <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden max-w-full min-w-0">
                            <div className="h-3 rounded-full bg-yellow-400" style={{ width: `${row.percent}%` }} />
                          </div>
                          <span className="w-10 text-right text-sm text-gray-500">{row.percent || 0}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Second row: filter/sort controls */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-6">
                    {/* Star Filter */}
                    <div className="flex flex-wrap items-center gap-2 gap-y-2 w-full">
                      <button
                        className={`px-3 py-1 rounded-full border text-sm font-medium ${selectedStar === null ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'}`}
                        onClick={() => { setSelectedStar(null); setCurrentPage(1); }}
                        type="button"
                      >
                        All
                      </button>
                      {[5, 4, 3, 2, 1].map(star => (
                        <button
                          key={star}
                          className={`flex items-center gap-1 px-3 py-1 rounded-full border text-sm font-medium ${selectedStar === star ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'}`}
                          onClick={() => { setSelectedStar(star); setCurrentPage(1); }}
                          type="button"
                        >
                          <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          {star}
                        </button>
                      ))}
                    </div>
                    {/* Sort Dropdown */}
                    <div>
                      <label className="mr-2 text-sm text-gray-700 font-medium" htmlFor="sort-reviews">Sort by:</label>
                      <select
                        id="sort-reviews"
                        className="border border-gray-300 rounded px-3 py-1 text-sm"
                        value={sortOption}
                        onChange={e => { setSortOption(e.target.value); setCurrentPage(1); }}
                      >
                        {SORT_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>


                <ShareExperienceSection
                  onWriteReviewClick={handleOpenReviewModal}
                  className="mb-6"
                />

                {/* Reviews List */}
                <div className="flex flex-col gap-6">
                  {reviewsLoading && (
                    <div className="text-center py-8">
                      <LoadingSpinner size="md" text="Loading reviews..." />
                    </div>
                  )}
                  {reviewsError && (
                    <div className="text-center text-red-500 py-8">Failed to load reviews. Please try again.</div>
                  )}
                  {!reviewsLoading && !reviewsError && transformedReviews.length === 0 && (
                    <div className="text-center text-gray-500 py-8">No reviews found.</div>
                  )}
                  {!reviewsLoading && !reviewsError && transformedReviews.map((review: { id: number; user: { initials: string; name: string }; rating: number; title: string; date: string; verified: boolean; text: string }) => (
                    <div key={review.id} className="bg-white border border-gray-200 rounded-xl p-3 sm:p-4 md:p-6 flex flex-col gap-3 w-full max-w-full">
                      {/* Top Row: Avatar, Info, Stars */}
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="flex flex-row gap-4 items-start">
                          {/* Avatar */}
                          <div className="w-14 h-14 rounded-lg bg-blue-600 flex items-center justify-center text-xl font-bold text-white">
                            {review.user.initials}
                          </div>
                          {/* Name and Status */}
                          <div className="flex flex-col justify-center">
                            <span className="font-semibold text-gray-900 text-base leading-tight">{review.user.name}</span>
                            <span className="text-sm text-gray-500 flex items-center gap-2 mt-0.5">
                              {review.verified && (
                                <span className="flex items-center gap-1">
                                  <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                                  Verified Purchase
                                </span>
                              )}
                              {review.verified && <span className="mx-1">•</span>}
                              {review.date}
                            </span>
                          </div>
                        </div>
                        {/* Stars */}
                        <div className="flex items-center gap-0.5 mt-2 sm:mt-0 justify-start sm:justify-end">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className={`w-5 h-5 ${i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                          ))}
                        </div>
                      </div>
                      {/* Title */}
                      <div className="font-semibold text-gray-900 text-lg mt-1">{review.title}</div>
                      {/* Text */}
                      <div className="text-gray-700 text-base leading-relaxed">{review.text}</div>
                    </div>
                  ))}
                </div>
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center gap-2 mt-10">
                    <button
                      className="px-3 py-1 rounded border border-gray-300 bg-white text-gray-700 disabled:opacity-50"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      type="button"
                    >
                      Previous
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        className={`px-3 py-1 rounded border text-sm font-medium ${currentPage === page ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'}`}
                        onClick={() => setCurrentPage(page)}
                        type="button"
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      className="px-3 py-1 rounded border border-gray-300 bg-white text-gray-700 disabled:opacity-50"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      type="button"
                    >
                      Next
                    </button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Write Review Modal */}
      <WriteReviewModal
        serviceId={service.id}
        serviceName={service.name}
        isOpen={isReviewModalOpen}
        onClose={handleCloseReviewModal}
        onSuccess={handleReviewSuccess}
      />
    </div>
  );
};

export default ServiceDetails;
